import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { Users, BarChart3, Mail, Shield, TrendingUp, Award, BookOpen, Brain, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
    {sub && <p className="text-xs text-slate-400 mt-2">{sub}</p>}
  </div>
);

export default function AdminPage() {
  const { user, getAuthHeaders } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    const headers = getAuthHeaders();
    try {
      const [statsRes, usersRes, reqRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/users`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/enterprise-requests`, { headers, withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setRequests(reqRes.data);
    } catch (err) {
      console.error('Admin fetch error:', err);
    }
    setLoading(false);
  };

  const updateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, { headers: getAuthHeaders(), withCredentials: true });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
    } catch (err) {
      console.error('Role update error:', err);
    }
  };

  const updateRequestStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/enterprise-requests/${requestId}/status?status=${status}`, {}, { headers: getAuthHeaders(), withCredentials: true });
      setRequests(prev => prev.map(r => r.request_id === requestId ? { ...r, status } : r));
    } catch (err) {
      console.error('Status update error:', err);
    }
  };

  const filteredUsers = users
    .filter(u => {
      const term = searchTerm.toLowerCase();
      return u.name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term);
    })
    .sort((a, b) => {
      const av = a[sortField] || '';
      const bv = b[sortField] || '';
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8ECF1] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: language === 'fr' ? 'Tableau de bord' : 'Dashboard', icon: BarChart3 },
    { id: 'users', label: language === 'fr' ? 'Utilisateurs' : 'Users', icon: Users },
    { id: 'requests', label: language === 'fr' ? 'Demandes Entreprise' : 'Enterprise Requests', icon: Mail },
  ];

  const statusColors = { new: 'bg-blue-100 text-blue-700', pending: 'bg-amber-100 text-amber-700', contacted: 'bg-green-100 text-green-700', closed: 'bg-slate-100 text-slate-500' };

  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="admin-page">
      <SEO
        title="Administration"
        description="Panneau d'administration IA Academy"
        path="/admin"
        noIndex
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }} data-testid="admin-title">
              {language === 'fr' ? 'Administration' : 'Administration'}
            </h1>
            <p className="text-sm text-slate-500">{language === 'fr' ? 'Gérez votre plateforme IA Academy' : 'Manage your IA Academy platform'}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-lg p-1 border border-slate-200 w-fit" data-testid="admin-tabs">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === t.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {tab === 'dashboard' && stats && (
          <div data-testid="admin-dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard icon={Users} label={language === 'fr' ? 'Utilisateurs' : 'Users'} value={stats.total_users} sub={`${stats.active_users_7d} ${language === 'fr' ? 'actifs cette semaine' : 'active this week'}`} color="bg-blue-600" />
              <StatCard icon={TrendingUp} label={language === 'fr' ? 'Inscriptions (30j)' : 'Signups (30d)'} value={stats.recent_signups_30d} color="bg-emerald-600" />
              <StatCard icon={BookOpen} label={language === 'fr' ? 'Modules terminés' : 'Modules completed'} value={stats.total_modules_done} color="bg-purple-600" />
              <StatCard icon={Brain} label={language === 'fr' ? 'Quiz passés' : 'Quizzes taken'} value={stats.total_quizzes_done} sub={`${language === 'fr' ? 'Score moyen' : 'Avg score'}: ${stats.avg_quiz_score}%`} color="bg-orange-500" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Award} label={language === 'fr' ? 'Certificats' : 'Certificates'} value={stats.total_certificates} color="bg-amber-500" />
              <StatCard icon={Mail} label={language === 'fr' ? 'Demandes entreprise' : 'Enterprise requests'} value={stats.enterprise_requests} color="bg-rose-500" />
              <div className="bg-white rounded-xl border border-slate-200 p-5 col-span-2" data-testid="plan-distribution">
                <p className="text-sm font-medium text-slate-700 mb-3">{language === 'fr' ? 'Distribution des plans' : 'Plan distribution'}</p>
                <div className="flex items-end gap-4 h-16">
                  {[
                    { label: 'Sans plan', count: stats.plan_distribution?.sans_plan || 0, color: 'bg-slate-300' },
                    { label: 'IA Starter', count: stats.plan_distribution?.decouverte || 0, color: 'bg-slate-500' },
                    { label: 'IA Fondations', count: stats.plan_distribution?.essentiel || 0, color: 'bg-blue-500' },
                    { label: 'IA Pro', count: stats.plan_distribution?.pro || 0, color: 'bg-orange-500' },
                    { label: 'IA Expert', count: stats.plan_distribution?.entreprise || 0, color: 'bg-emerald-500' },
                  ].map(p => {
                    const max = Math.max(stats.plan_distribution?.sans_plan || 0, stats.plan_distribution?.decouverte || 0, stats.plan_distribution?.essentiel || 0, stats.plan_distribution?.pro || 0, stats.plan_distribution?.entreprise || 0, 1);
                    const h = Math.max(12, (p.count / max) * 64);
                    return (
                      <div key={p.label} className="flex flex-col items-center gap-1 flex-1">
                        <span className="text-xs font-bold text-slate-700">{p.count}</span>
                        <div className={`w-full rounded-t ${p.color}`} style={{ height: `${h}px` }} />
                        <span className="text-[10px] text-slate-500">{p.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div data-testid="admin-users">
            <div className="mb-4 flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder={language === 'fr' ? 'Rechercher un utilisateur...' : 'Search users...'}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="admin-user-search"
                />
              </div>
              <Badge variant="outline">{filteredUsers.length} {language === 'fr' ? 'utilisateurs' : 'users'}</Badge>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-users-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 cursor-pointer" onClick={() => toggleSort('name')}>
                        <span className="flex items-center gap-1">{language === 'fr' ? 'Nom' : 'Name'} <SortIcon field="name" /></span>
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 cursor-pointer" onClick={() => toggleSort('email')}>
                        <span className="flex items-center gap-1">Email <SortIcon field="email" /></span>
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">{language === 'fr' ? 'Rôle' : 'Role'}</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">{language === 'fr' ? 'Plan' : 'Plan'}</th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600">{language === 'fr' ? 'Modules' : 'Modules'}</th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600">{language === 'fr' ? 'Quiz' : 'Quizzes'}</th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600">{language === 'fr' ? 'Streak' : 'Streak'}</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 cursor-pointer" onClick={() => toggleSort('created_at')}>
                        <span className="flex items-center gap-1">{language === 'fr' ? 'Inscription' : 'Joined'} <SortIcon field="created_at" /></span>
                      </th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u, i) => (
                      <tr key={u.user_id} className={`border-b border-slate-100 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`} data-testid={`admin-user-row-${i}`}>
                        <td className="px-4 py-3 font-medium text-slate-900">{u.name || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{u.email}</td>
                        <td className="px-4 py-3">
                          <Badge className={u.role === 'admin' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}>
                            {u.role || 'learner'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs capitalize">{u.plan || 'free'}</Badge>
                        </td>
                        <td className="px-4 py-3 text-center text-slate-700">{u.modules_completed}</td>
                        <td className="px-4 py-3 text-center text-slate-700">{u.quizzes_completed}</td>
                        <td className="px-4 py-3 text-center text-slate-700">{u.current_streak || 0}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{u.created_at?.slice(0, 10) || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          {u.user_id !== user.user_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs"
                              onClick={() => updateRole(u.user_id, u.role === 'admin' ? 'learner' : 'admin')}
                              data-testid={`toggle-role-${u.user_id}`}
                            >
                              {u.role === 'admin'
                                ? (language === 'fr' ? 'Retirer admin' : 'Remove admin')
                                : (language === 'fr' ? 'Promouvoir admin' : 'Make admin')}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Enterprise Requests Tab */}
        {tab === 'requests' && (
          <div data-testid="admin-requests">
            {requests.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">{language === 'fr' ? 'Aucune demande entreprise pour le moment.' : 'No enterprise requests yet.'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map((r, i) => (
                  <div key={r.request_id || i} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`admin-request-${i}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900">{r.name} — <span className="font-normal text-slate-600">{r.company}</span></h3>
                        <p className="text-sm text-slate-500">{r.email}</p>
                      </div>
                      <Badge className={statusColors[r.status] || statusColors.new}>{r.status || 'new'}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                      <span>{language === 'fr' ? 'Collaborateurs' : 'Employees'}: <strong>{r.employees_count}</strong></span>
                      <span>{language === 'fr' ? 'Reçu le' : 'Received'}: {r.created_at?.slice(0, 10)}</span>
                    </div>
                    {r.message && <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 mb-3">{r.message}</p>}
                    <div className="flex gap-2">
                      {['contacted', 'closed'].map(s => (
                        <Button
                          key={s}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => updateRequestStatus(r.request_id, s)}
                          disabled={r.status === s}
                          data-testid={`request-${i}-${s}`}
                        >
                          {s === 'contacted'
                            ? (language === 'fr' ? 'Marquer contacté' : 'Mark contacted')
                            : (language === 'fr' ? 'Clôturer' : 'Close')}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
